import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search, FileText } from 'lucide-react';
import { ROUTES, postPath } from '../routes';
import { NAV_ITEMS } from './Sidebar';
import type { BlogPost } from '../types/calculator';

// Matches "35000", "75k", "£45,000" — anything meant as a salary shortcut
// rather than a text search for a calculator or article.
const NUMERIC_LIKE = /^£?[\d,]+k?$/i;

interface SearchBoxProps {
  variant?: 'hero' | 'compact';
  placeholder?: string;
  className?: string;
}

export const SearchBox: React.FC<SearchBoxProps> = ({
  variant = 'hero',
  placeholder = 'Search calculators & articles, or type a salary...',
  className = '',
}) => {
  const navigate = useNavigate();
  const [quickValue, setQuickValue] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [blogPosts, setBlogPosts] = useState<BlogPost[] | null>(null);
  const blurTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const blogPostsRequested = useRef(false);

  // Article data (~250KB) is only fetched once the user shows intent to
  // search, not bundled eagerly — this component renders in the TopBar on
  // every page, so an eager import would bloat the critical-path bundle.
  const loadBlogPosts = useCallback(() => {
    if (blogPostsRequested.current) return;
    blogPostsRequested.current = true;
    import('../data/blogPosts').then((m) => setBlogPosts(m.BLOG_POSTS));
  }, []);

  const query = quickValue.trim().toLowerCase();
  const isNumericQuery = query.length > 0 && NUMERIC_LIKE.test(query);

  // Numeric queries (e.g. "75") still get matched against calculators/articles —
  // "75" should surface the "£75,000 after tax" guide, not just silently jump to
  // the calculator with a nonsensical £75/year salary. The numeric shortcut is
  // offered alongside the dropdown, not instead of it.
  const calcMatches = useMemo(() => {
    if (!query) return [];
    return NAV_ITEMS.filter(
      (item) => item.label.toLowerCase().includes(query) || item.desc?.toLowerCase().includes(query)
    ).slice(0, 4);
  }, [query]);

  const blogMatches = useMemo(() => {
    if (!query || !blogPosts) return [];
    return blogPosts.filter(
      (p) =>
        p.title.toLowerCase().includes(query) ||
        p.shortDescription.toLowerCase().includes(query) ||
        p.targetKeyword.toLowerCase().includes(query)
    ).slice(0, 5);
  }, [query, blogPosts]);

  const showDropdown = searchFocused && query.length > 0 && (calcMatches.length > 0 || blogMatches.length > 0 || isNumericQuery);

  const parsedSalary = useMemo(() => {
    if (!isNumericQuery) return null;
    const raw = query.replace(/[£,]/g, '');
    const multiplier = raw.endsWith('k') ? 1000 : 1;
    const numeric = parseFloat(raw.replace(/k$/, ''));
    return isNaN(numeric) ? null : Math.round(numeric * multiplier);
  }, [query, isNumericQuery]);

  const goToSalary = () => {
    if (parsedSalary === null) return;
    setQuickValue('');
    setSearchFocused(false);
    navigate(`${ROUTES.calculator}?salary=${parsedSalary}`);
  };

  const goTo = (path: string) => {
    setQuickValue('');
    setSearchFocused(false);
    navigate(path);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (parsedSalary !== null) {
      goToSalary();
    } else if (query) {
      navigate(`${ROUTES.blog}?search=${encodeURIComponent(quickValue.trim())}`);
      setQuickValue('');
      setSearchFocused(false);
    } else {
      navigate(ROUTES.calculator);
    }
  };

  const isHero = variant === 'hero';

  return (
    <form onSubmit={handleSubmit} className={`relative text-left ${className}`}>
      <div
        className={`flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-2xl focus-within:border-emerald-500 transition-colors ${
          isHero ? 'p-2 shadow-xl' : 'p-1'
        }`}
      >
        <Search className={`text-zinc-500 shrink-0 ${isHero ? 'w-5 h-5 ml-2' : 'w-4 h-4 ml-2'}`} />
        <input
          type="text"
          value={quickValue}
          onChange={(e) => setQuickValue(e.target.value)}
          onFocus={() => {
            clearTimeout(blurTimeout.current);
            setSearchFocused(true);
            loadBlogPosts();
          }}
          onBlur={() => {
            // Small delay so a click on a dropdown result registers before it unmounts.
            blurTimeout.current = setTimeout(() => setSearchFocused(false), 150);
          }}
          placeholder={placeholder}
          aria-label="Search calculators and articles, or enter a salary"
          className={`flex-1 bg-transparent text-white placeholder-zinc-500 outline-none min-w-0 ${
            isHero ? 'text-sm sm:text-base' : 'text-xs'
          }`}
        />
        <button
          type="submit"
          className={`inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-extrabold rounded-xl shadow-lg transition-colors whitespace-nowrap ${
            isHero ? 'px-4 sm:px-5 py-2.5 text-sm' : 'px-3 py-1.5 text-xs'
          }`}
        >
          <span>{isNumericQuery ? 'Check Pay' : 'Search'}</span>
          <ArrowRight className={isHero ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
        </button>
      </div>

      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden z-30 max-h-96 overflow-y-auto">
          {parsedSalary !== null && (
            <div className="p-2">
              <button
                type="button"
                onClick={goToSalary}
                className="w-full flex items-center gap-3 px-2.5 py-2 rounded-xl hover:bg-emerald-500/10 text-left transition-colors"
              >
                <Search className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="text-sm font-semibold text-white">
                  Calculate take-home pay for <span className="text-emerald-400">£{parsedSalary.toLocaleString()}</span>
                </span>
              </button>
            </div>
          )}
          {calcMatches.length > 0 && (
            <div className={`p-2 ${parsedSalary !== null ? 'border-t border-zinc-800/80' : ''}`}>
              <div className="px-2.5 py-1 text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Calculators</div>
              {calcMatches.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goTo(ROUTES[item.id as keyof typeof ROUTES] || ROUTES.calculator)}
                  className="w-full flex items-center gap-3 px-2.5 py-2 rounded-xl hover:bg-zinc-900 text-left transition-colors"
                >
                  <span className="shrink-0">{item.icon}</span>
                  <span className="text-sm font-semibold text-white">{item.label}</span>
                </button>
              ))}
            </div>
          )}
          {blogMatches.length > 0 && (
            <div className="p-2 border-t border-zinc-800/80">
              <div className="px-2.5 py-1 text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Articles</div>
              {blogMatches.map((post) => (
                <button
                  key={post.id}
                  type="button"
                  onClick={() => goTo(postPath(post.slug))}
                  className="w-full flex items-center gap-3 px-2.5 py-2 rounded-xl hover:bg-zinc-900 text-left transition-colors"
                >
                  <FileText className="w-4 h-4 text-amber-400 shrink-0" />
                  <span className="text-sm font-semibold text-white truncate">{post.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </form>
  );
};
